<template>
  <div class="overflow-x-hidden">
    <TripSwapContentSkeleton v-if="isFetching" />

    <template v-else>
      <TripSwapContentComplete
        v-if="step === SwapStep.Complete && acceptData?.data.data"
        :data="acceptData.data.data"
      >
        <JButton
          v-if="jmBookConfirmation"
          color="primary"
          block
          class="rounded-md"
          @click="redirectToTripDetails(jmBookConfirmation)"
        >
          {{ $t('review') }} {{ $t('trips.tripDetails') }}

          <JIcon name="arrow-right" size="16" />
        </JButton>
      </TripSwapContentComplete>

      <template v-else>
        <template v-if="!isError && swapStore.swapId">
          <div class="p-24">
            <component :is="contentComponent" :data="data?.data" />
          </div>
          <JRow
            spaceSm="8"
            spaceMd="8"
            spaceLg="8"
            spaceXl="8"
            class="border-t border-line p-24"
          >
            <JCol sm="6" md="6" lg="6" xl="6">
              <JButton
                color="gray-100"
                block
                class="rounded-md !border-line"
                :isLoading="rejectIsLoading"
                :disabled="isExpired"
                @click="onRejectClick"
              >
                {{
                  step === SwapStep.Review
                    ? $t('words.cancel')
                    : $t('words.reject')
                }}
              </JButton>
            </JCol>
            <JCol sm="6" md="6" lg="6" xl="6">
              <JButton
                color="primary"
                block
                class="rounded-md"
                :isLoading="acceptIsLoading"
                :disabled="isExpired"
                @click="onAcceptClick"
              >
                {{
                  step === SwapStep.Review
                    ? $t('words.confirm')
                    : $t('words.accept')
                }}
              </JButton>
            </JCol>
          </JRow>
        </template>

        <div v-else>
          <div class="p-24 text-center text-pane-header text-gray-500">
            {{ $t('messages.unauthorizedViewSwapOffer') }}
          </div>
          <JRow
            spaceSm="8"
            spaceMd="8"
            spaceLg="8"
            spaceXl="8"
            class="border-t border-line px-24 py-12"
          >
            <JCol>
              <JButton
                color="gray-100"
                block
                class="rounded-md !border-line"
                @click="onClose"
              >
                {{ $t('words.close') }}
              </JButton>
            </JCol>
          </JRow>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, defineAsyncComponent, watch } from 'vue'
import { JButton, JCol, JIcon, JRow } from '@journey/ui-kit'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import TripSwapContentSkeleton from './TripSwapContentSkeleton/TripSwapContentSkeleton.vue'
import type { Emits } from './TripSwapContent.types'
import {
  SwapConfirmationStatus,
  SwapStep,
  type SwapType,
} from '@/modules/Messages/types'
import useSwapStore from '@/modules/Messages/store/swap'
import { useFetchSwap } from '@/modules/Messages/requests/queries'
import {
  useAcceptSwapMutation,
  useRejectSwapMutation,
} from '@/modules/Messages/requests/mutations'

const emit = defineEmits<Emits>()

const TripSwapContentComplete = defineAsyncComponent(
  () => import('./TripSwapContentComplete/TripSwapContentComplete.vue')
)
const TripSwapContentHotel = defineAsyncComponent(
  () => import('./TripSwapContentHotel/TripSwapContentHotel.vue')
)
const TripSwapContentHotelConfirmation = defineAsyncComponent(
  () =>
    import(
      './TripSwapContentHotelConfirmation/TripSwapContentHotelConfirmation.vue'
    )
)

const swapStore = useSwapStore()
const { step, swapId, swapType } = storeToRefs(swapStore)

const id = computed(() => swapId?.value || '')
const enabled = computed(() => !!id.value.length)

const { data, isFetching, isError } = useFetchSwap(
  {
    id,
  },
  {
    enabled,
  }
)

const isExpired = computed(() => {
  if (!data.value?.data) {
    return false
  }

  const remainingTimeInMinutes = data.value.data.hotelOffers?.reduce(
    (acc, offer) => {
      const remaining = offer?.offeredBooking?.remainingTimeInMinutes
      return acc + (typeof remaining === 'number' ? remaining : 0)
    },
    0
  )

  return remainingTimeInMinutes === 0
})

watch(data, (value) => {
  if (!value?.data) {
    return
  }

  if ('hotelOffers' in value.data) {
    swapStore.setSwapType('hotel')
  }
})

watch(
  isFetching,
  (value) => {
    swapStore.toggleIsLoading(value)
  },
  {
    immediate: true,
  }
)

const {
  mutateAsync: acceptMutate,
  data: acceptData,
  isLoading: acceptIsLoading,
} = useAcceptSwapMutation()

async function onAcceptClick() {
  // CONFIRM
  if (step.value === SwapStep.Review) {
    await acceptMutate({
      id,
    })

    const status = acceptData.value?.data.data.status

    if (
      status === SwapConfirmationStatus.Accepted ||
      status === SwapConfirmationStatus.Pending
    ) {
      step.value = SwapStep.Complete
    }

    return
  }

  // ACCEPT
  step.value = SwapStep.Review
}

const { mutateAsync: rejectMutate, isLoading: rejectIsLoading } =
  useRejectSwapMutation()

async function onRejectClick() {
  // CANCEL
  if (step.value === SwapStep.Review) {
    step.value = SwapStep.Select
    return
  }

  // REJECT
  await rejectMutate({
    id,
  })

  swapStore.resetSwapState()

  emit('close')
}

const contentComponent = computed(() => {
  const contentComponents: { [key in SwapType]: Component | null } = {
    hotel: TripSwapContentHotel,
    car: null,
    flight: null,
  }

  const comfirmationComponents: { [key in SwapType]: Component | null } = {
    hotel: TripSwapContentHotelConfirmation,
    car: null,
    flight: null,
  }

  const components =
    step.value === SwapStep.Review ? comfirmationComponents : contentComponents

  return Object.entries(components).find(
    ([key]) => key === swapType?.value
  )?.[1]
})

const jmBookConfirmation = computed(() => {
  const hotel = acceptData.value?.data.data.swapOfferHotels?.[0]

  if (hotel) {
    return hotel.jmBookingConfirmation
  }

  return null
})

const router = useRouter()

async function redirectToTripDetails(jmBookConfirmation: string) {
  await router.replace({ name: 'Trip', params: { jmBookConfirmation } })
  swapStore.resetSwapState()
}

function onClose() {
  swapStore.resetSwapState()
  emit('close')
}
</script>
